import {
  Box,
  Checkbox,
  Divider,
  Image,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  useToast,
} from "@chakra-ui/react";
import { useState } from "react";
import Confetti from "react-confetti";
import ConfettiEmoji from "../../../images/confetti.png";

import { BannerAlert, Heading, Text } from "@equidefi/ui";
import { WorkflowButton } from "../../../components/workflow/WorkflowButton";
import { useSavedProfileUpsert } from "../../../hooks/useSavedProfileUpsert";

const getErrorMessage = (error) => {
  if (Array.isArray(error?.response?.data?.errors)) {
    return error.response.data.errors.join(". ");
  }
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  if (error?.message) {
    return error.message;
  }
  return "Something went wrong. Please try again.";
};

export const CompletedModal = ({ modal, vaultId, accountType, isManualPayment, hasAdditionalSigners, expedited }) => {
  const toast = useToast();
  const { upsertProfile, isSaving, profileExists, isReady } = useSavedProfileUpsert(vaultId, accountType);
  const [shouldSaveProfile, setShouldSaveProfile] = useState(true);

  // Expedited investments reuse an existing vault unchanged, so there is nothing
  // new to persist to the investor's saved profile.
  const canSaveProfile = !expedited;

  const handleContinue = async () => {
    try {
      if (canSaveProfile && shouldSaveProfile) {
        await upsertProfile();
      }
      modal.onClose();
    } catch (error) {
      console.error(error);
      toast({
        status: "error",
        description: getErrorMessage(error),
      });
    }
  };

  return (
    <Modal
      isOpen={modal.isOpen}
      onClose={modal.onClose}
      aria-labelledby="contained-modal-title-vcenter"
      motionPreset="slideInBottom"
      scrollBehavior="inside"
      isCentered>
      <ModalOverlay>
        <Confetti style={{ position: "fixed", width: "100vw", height: "100vh" }} />
      </ModalOverlay>

      <ModalContent m="4" maxH="full">
        <ModalHeader>
          <Box display="flex" justifyContent="center" mb="3">
            <Image
              src={ConfettiEmoji}
              alt=""
              w={{ base: "40px", md: "80px" }}
              h={{ base: "40px", md: "80px" }}
              objectFit="contain"
            />
            <Heading fontWeight="bold" m="1">
              Congratulations!
            </Heading>
          </Box>
          <Text fontSize="sm" color="gray.600" fontWeight="normal" m="0">
            Your investment has been submitted successfully.
          </Text>
        </ModalHeader>

        <ModalBody>
          {isManualPayment && (
            <BannerAlert
              status="info"
              title="Manual Payment Indicated"
              description="Instructions for submitting your payment have been sent to your email. Your investment cannot be reviewed or accepted until your payment has been received."
              mb={4}
            />
          )}

          {hasAdditionalSigners && (
            <BannerAlert
              status="info"
              title="Additional Signers or UBO's Added"
              description="Email invitations have been sent to the additional signers or UBO's to complete the investment process. Your investment cannot be reviewed or accepted until all required parties have signed."
              mb={4}
            />
          )}

          {canSaveProfile && (
            <>
              <Divider mb="4" />

              <Box border="1px solid" borderColor="gray.500" borderRadius="lg" bg="gray.50" p="4" mb="3">
                <Text fontSize="md" fontWeight="700" mb="3" m="0">
                  Make Investing Faster (optional)
                </Text>

                <Checkbox
                  isChecked={shouldSaveProfile}
                  onChange={(e) => setShouldSaveProfile(e.target.checked)}
                  isDisabled={isSaving}
                  colorScheme="blue"
                  size="md">
                  <Text fontSize="sm" m="0">
                    {profileExists?.exists
                      ? "Update my saved personal information with the most recent data."
                      : "Save my personal information to make future investments faster and easier."}
                  </Text>
                </Checkbox>
              </Box>

              <Text fontSize="xs" color="gray.500" m="0">
                We respect your privacy. All personal information is securely stored with AES-256 encryption.
              </Text>
            </>
          )}
        </ModalBody>

        <ModalFooter>
          <WorkflowButton
            onClick={handleContinue}
            isLoading={isSaving || (canSaveProfile && shouldSaveProfile && !isReady)}
            w="full">
            Continue To Portfolio
          </WorkflowButton>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
